import { useEffect, useRef, useState } from "react";
import Logo from "@/assets/logo.svg";
import DesignHinge from "@/assets/design-hinge.svg";
import { useNavigate } from "react-router";
import { IoIosArrowDown } from "react-icons/io";
import { Link } from "react-router-dom";
import useDropdown from "@/utils/hooks/useDropdown";
import useUserStore from "@/utils/Stores/userStore";
import { useQueryClient } from "@tanstack/react-query";

import Loader from "./Loader";
import { FiLogOut } from "react-icons/fi";
import { RiSettings5Line } from "react-icons/ri";
import ImgPlaceholder from "@/assets/img-placeholder.svg";
import useViewStyle from "@/utils/Stores/viewStyleStore";
import useWindowSize from "@/utils/hooks/useWindowSize";
import { IoMdMenu } from "react-icons/io";
import MobileMenu from "../MobileMenu";
import PrimaryBtn from "./Button";
import Modal from "./Modal";
import { CreateTask } from "@/pageListAsync";
const Navbar = () => {
  const { isMobile } = useWindowSize();
  const queryClient = useQueryClient();
  const isLogged = useUserStore((state: any) => state.isLogged);
  const logout = useUserStore((state: any) => state.logout);
  const user = useUserStore((state: any) => state.user);
  const selectedWorkSpace = useUserStore(
    (state: any) => state.selectedWorkSpace
  );
  const changeWorkSpace = useUserStore((state: any) => state.changeWorkSpace);
  const changeTenantID = useUserStore((state: any) => state.changeTenantID);
  const tenantId = useUserStore((state: any) => state.tenantId);
  const selectedMenu = useViewStyle((state: any) => state.selectedMenu);
  const setSelectedMenu = useViewStyle((state: any) => state.setSelectedMenu);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);

  const navigationItems = [
    { label: "specifications", path: "/specifications" },
    { label: "projects", path: "/project" },
    { label: "vendors", path: "/vendors" },
    { label: "clients", path: "/clients" },
    { label: "company/companyprofile", path: "/companyprofile" },
    { label: "company/team", path: "/team" },
    { label: "company/sales-tax", path: "/sales-tax" },
    { label: "company/sales-category", path: "/sales-category" },
    { label: "timesheet/timesheet", path: "/timesheet" },
    { label: "timesheet/approval", path: "/approval" },
  ];

  useEffect(() => {
    const path = `/${location.pathname.split("/")[1]}`;

    if (!selectedMenu) {
      // Map path to menu
      let menu = navigationItems?.find((item) => item.path === path)?.label;
      setSelectedMenu(menu);
    }
  }, []);

  const { fetchUserLoading } = useUserStore();

  const dropDownRef = useRef();
  const mobileMenuRef = useRef();
  const workSpaceRef = useRef();
  const tenantIDRef = useRef();
  const profileRef = useRef();

  const navigate = useNavigate();
  const { dropdownOpen, setDropdownOpen } = useDropdown(dropDownRef);
  const { dropdownOpen: mobileMenu, setDropdownOpen: setMobileMenu } =
    useDropdown(mobileMenuRef);

  const {
    dropdownOpen: workSpaceDropDown,
    setDropdownOpen: setWorkSpaceDropDown,
  } = useDropdown(workSpaceRef);

  const {
    dropdownOpen: tenantIDDropDown,
    setDropdownOpen: setTenantIDDropDown,
  } = useDropdown(tenantIDRef);

  const { dropdownOpen: profileDropdown, setDropdownOpen: setProfileDropdown } =
    useDropdown(profileRef);

  const timesheetDropdownRef = useRef();
  const {
    dropdownOpen: timesheetDropdown,
    setDropdownOpen: setTimesheetDropdown,
  } = useDropdown(timesheetDropdownRef);

  const handleMenuClick = (menu: string) => {
    setSelectedMenu(menu, selectedMenu);
  };

  const handleCloseAddTaskModal = () => {
    setShowAddTaskModal(false);
  };

  return (
    <>
      {showAddTaskModal && (
        <Modal
          title=""
          handleClose={handleCloseAddTaskModal}
          width="max-w-[80dvw]"
        >
          <CreateTask isModal={true} handleClose={handleCloseAddTaskModal} />
        </Modal>
      )}
      <nav
        className={`flex items-center justify-between px-6 py-5 border-b border-customGrey-primary bg-[#E5F0EF] 
    text-black-secondary font-lato text-base h-[84px] sticky top-0 z-[99]`}
      >
        {/* Logo */}
        <div className="flex-shrink-0">
          <img
            src={DesignHinge}
            alt="Logo"
            className="cursor-pointer"
            onClick={() => {
              navigate("/");
              handleMenuClick("specifications");
            }}
          />
        </div>

        {isMobile && (
          <div
            className="flex items-center cursor-pointer"
            onClick={() => setMobileMenu((prev) => !prev)}
          >
            <IoMdMenu className="text-2xl" />
          </div>
        )}

        {/* Mobile Menu */}
        <MobileMenu
          mobileMenu={mobileMenu}
          setMobileMenu={setMobileMenu}
          handleMenuClick={handleMenuClick}
          selectedMenu={selectedMenu}
        />

        {/* Navigation Tabs */}
        <div
          className={`flex space-x-4 text-base items-center ${
            isMobile ? "hidden" : ""
          }`}
        >
          <div className="relative group">
            <Link
              to="/specifications"
              className={`px-4 py-2 focus:outline-none whitespace-nowrap hover:text-customGreen-text ${
                selectedMenu == "specifications"
                  ? "bg-customGreen-bg text-customGreen-text"
                  : ""
              }`}
              onClick={() => handleMenuClick("specifications")}
            >
              Design Library
            </Link>
          </div>
          <div className="relative group">
            <Link
              to="/projects"
              className={`px-4 py-2 focus:outline-none hover:text-customGreen-text ${
                selectedMenu == "projects"
                  ? "bg-customGreen-bg text-customGreen-text"
                  : ""
              }`}
              onClick={() => handleMenuClick("projects")}
            >
              Projects
            </Link>
          </div>
          <div className="relative group">
            <Link
              to="/vendors"
              className={`px-4 py-2 focus:outline-none hover:text-customGreen-text ${
                selectedMenu == "vendors"
                  ? "bg-customGreen-bg text-customGreen-text"
                  : ""
              }`}
              onClick={() => handleMenuClick("vendors")}
            >
              Vendors
            </Link>
          </div>
          <div className="relative group">
            <Link
              to="/clients"
              className={`px-4 py-2 focus:outline-none hover:text-customGreen-text ${
                selectedMenu == "clients"
                  ? "bg-customGreen-bg text-customGreen-text"
                  : ""
              }`}
              onClick={() => handleMenuClick("clients")}
            >
              Clients
            </Link>
          </div>
          <div className="relative group" ref={dropDownRef}>
            <button
              className={`px-4 py-2 focus:outline-none flex justify-between items-center hover:text-customGreen-text ${
                selectedMenu?.split("/")[0] == "company"
                  ? "bg-customGreen-bg text-customGreen-text"
                  : ""
              }`}
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              Company
              <span className="ml-2 my-auto">
                <IoIosArrowDown />
              </span>
            </button>
            {dropdownOpen && (
              <div className="absolute z-[99] left-0 w-48 py-2 font-inter bg-white text-black rounded-lg shadow-lg text-sm border border-gray-100">
                <Link
                  to="/companyprofile"
                  className={`block px-4 py-2 hover:bg-gray-25 ${
                    selectedMenu?.split("/")[1] == "companyprofile"
                      ? "bg-customGreen-bg text-customGreen-text"
                      : ""
                  }`}
                  onClick={() => {
                    setDropdownOpen(false);
                    handleMenuClick("company/companyprofile");
                  }}
                >
                  Company Profile
                </Link>
                <Link
                  to="/team"
                  className={`block px-4 py-2 hover:bg-gray-25  ${
                    selectedMenu?.split("/")[1] == "team"
                      ? "bg-customGreen-bg text-customGreen-text"
                      : ""
                  }`}
                  onClick={() => {
                    setDropdownOpen(false);
                    handleMenuClick("company/team");
                  }}
                >
                  Team
                </Link>

                <Link
                  to="/sales-tax"
                  className={`block px-4 py-2 hover:bg-gray-25  ${
                    selectedMenu?.split("/")[1] == "sales-tax"
                      ? "bg-customGreen-bg text-customGreen-text"
                      : ""
                  }`}
                  onClick={() => {
                    setDropdownOpen(false);
                    handleMenuClick("company/sales-tax");
                  }}
                >
                  Sales Tax Groups
                </Link>
                <Link
                  to="/sales-category"
                  className={`block px-4 py-2 hover:bg-gray-25  ${
                    selectedMenu?.split("/")[1] == "sales-category"
                      ? "bg-customGreen-bg text-customGreen-text"
                      : ""
                  }`}
                  onClick={() => {
                    setDropdownOpen(false);
                    handleMenuClick("company/sales-category");
                  }}
                >
                  Sales Categories
                </Link>
              </div>
            )}
          </div>
          <div className="relative group" ref={timesheetDropdownRef}>
            <button
              className={`px-4 py-2 focus:outline-none flex justify-between items-center hover:text-customGreen-text ${
                selectedMenu?.split("/")[0] == "timesheet"
                  ? "bg-customGreen-bg text-customGreen-text"
                  : ""
              }`}
              onClick={() => {
                // if (user?.role === "admin") {
                setTimesheetDropdown((prev) => !prev);
                // } else {
                //   navigate("/timesheet");
                // }
              }}
            >
              Timesheet
              {/* {user?.role === "admin" && ( */}
              <span className="ml-2 my-auto">
                <IoIosArrowDown />{" "}
              </span>
              {/* )} */}
            </button>

            {timesheetDropdown && (
              <div className="absolute z-[99] left-0 w-48 py-2 font-inter bg-white text-black rounded-lg shadow-lg text-sm border border-gray-100">
                <Link
                  to="/timesheet"
                  className={`block px-4 py-2 hover:bg-gray-25 ${
                    selectedMenu?.split("/")[1] == "timesheet"
                      ? "bg-customGreen-bg text-customGreen-text"
                      : ""
                  }`}
                  onClick={() => {
                    setTimesheetDropdown(false);
                    handleMenuClick("timesheet/timesheet");
                  }}
                >
                  Timesheet
                </Link>
                {user?.role != "user" && (
                  <Link
                    to="/approval"
                    className={`block px-4 py-2 hover:bg-gray-25 ${
                      selectedMenu?.split("/")[1] == "approval"
                        ? "bg-customGreen-bg text-customGreen-text"
                        : ""
                    }`}
                    onClick={() => {
                      setTimesheetDropdown(false);
                      handleMenuClick("timesheet/approval");
                    }}
                  >
                    Approval
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div
          className={`flex items-center space-x-4 font-lato text-base ${
            isMobile ? "hidden" : ""
          }`}
        >
          <PrimaryBtn
            text="Create Task"
            onClick={() => setShowAddTaskModal(true)}
          />
          {/* WorkSpace Dropdown */}
          {user?.allWorkSpace?.length > 1 && (
            <div className="relative group" ref={workSpaceRef}>
              <button
                className={`px-4 py-2 focus:outline-none flex justify-between `}
                onClick={() => setWorkSpaceDropDown((prev) => !prev)}
              >
                {
                  user?.allWorkSpace?.find(
                    (item: any) => item?._id == selectedWorkSpace
                  )?.name
                }
                <span className="ml-2 py-1.5">
                  <IoIosArrowDown />{" "}
                </span>
              </button>
              {workSpaceDropDown && (
                <div className="absolute z-[99] left-0 w-48 py-2 font-inter bg-white text-black rounded-lg shadow-lg text-sm border border-gray-100">
                  {user?.allWorkSpace?.map((workSpace: any) => (
                    <button
                      key={workSpace._id}
                      className={`block w-full text-start px-4 py-2 hover:bg-gray-25 ${
                        workSpace?._id == selectedWorkSpace
                          ? "bg-customGreen-bg text-customGreen-text"
                          : ""
                      }`}
                      onClick={() => {
                        changeWorkSpace(workSpace._id, queryClient, navigate);
                        setWorkSpaceDropDown((prev) => !prev);
                      }}
                    >
                      {workSpace?.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Organization Dropdown */}
          {user?.tenantID?.length > 1 && (
            <div className="relative group" ref={tenantIDRef}>
              <button
                className={`px-4 py-2 focus:outline-none flex justify-between `}
                onClick={() => setTenantIDDropDown((prev) => !prev)}
              >
                {fetchUserLoading ? (
                  <Loader />
                ) : (
                  user?.tenantID?.find((item) => item._id == tenantId)?.name
                )}
                <span className="ml-2 py-1.5">
                  <IoIosArrowDown />
                </span>
              </button>
              {tenantIDDropDown && (
                <div className="absolute z-[99] left-0 w-48 py-2 font-inter bg-white text-black rounded-lg shadow-lg text-sm border border-gray-100">
                  {user?.tenantID?.map((tenantID) => (
                    <button
                      key={tenantID._id}
                      className={`block w-full text-start px-4 py-2 hover:bg-gray-25 ${
                        tenantID?._id == tenantId
                          ? "bg-customGreen-bg text-customGreen-text"
                          : ""
                      }`}
                      onClick={() => {
                        changeTenantID(tenantID?._id, queryClient);
                        setTenantIDDropDown((prev) => !prev);
                      }}
                    >
                      {tenantID?.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {
            isLogged && (
              <div className="relative group" ref={profileRef}>
                <button
                  className={`px-4 py-2 focus:outline-none bg-customGreen-bg text-customGreen-text`}
                  onClick={() => setProfileDropdown((prev) => !prev)}
                >
                  <img
                    src={
                      user?.company_details?.logo
                        ? user?.company_details?.logo
                        : ImgPlaceholder
                    }
                    alt="Profile"
                    className="size-7 object-cover hover:bg-customGreen-bg rounded-full"
                  />
                </button>
                {profileDropdown && (
                  <div className="absolute z-[99] right-0 w-64 py-2 bg-white rounded-lg shadow-lg text-sm border border-gray-100">
                    {/* User Profile Section */}
                    <div className="px-4 py-2 ">
                      <div className="flex items-center gap-3">
                        <img
                          src={user?.company_details?.logo || ImgPlaceholder}
                          alt="Profile"
                          className="size-10 object-cover rounded-full"
                        />
                        <div>
                          <div className="font-semibold text-black-secondary text-sm">
                            {user?.company_details?.name}
                          </div>
                          <div className="text-gray-500 text-sm break-all">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Workspaces Section */}
                    {user?.allWorkSpace?.length > 0 && (
                      <div className="">
                        {user?.allWorkSpace?.map((workSpace: any) => (
                          <button
                            key={workSpace._id}
                            className={`block w-full text-start px-4 py-2 hover:bg-gray-25 ${
                              workSpace?._id == selectedWorkSpace
                                ? " font-medium "
                                : ""
                            }`}
                            onClick={() => {
                              changeWorkSpace(
                                workSpace._id,
                                queryClient,
                                navigate
                              );
                              setProfileDropdown(false);
                            }}
                          >
                            {workSpace?.name}{" "}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Settings & Logout Section */}
                    <div className="">
                      <div className="flex items-center w-full text-start px-3 py-2 hover:bg-gray-25">
                        <span className="text-lg text-customGrey-text ">
                          <RiSettings5Line />
                        </span>
                        <button
                          className="flex items-center w-full text-start px-2 hover:bg-gray-25"
                          onClick={() => {
                            navigate("/account/settings");
                            setProfileDropdown(false);
                            setSelectedMenu(null);
                          }}
                        >
                          Settings
                        </button>
                      </div>
                      <button
                        className="flex items-center w-full text-start px-4 py-2 hover:bg-gray-25 "
                        onClick={() => {
                          logout();
                          navigate("/login");
                          setSelectedMenu(null);
                          setProfileDropdown(false);
                        }}
                      >
                        <span className="mr-2">
                          <FiLogOut />
                        </span>
                        Log out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
            //   <div className="flex-shrink-0">
            //   <img
            //     src={Avatar}
            //     alt="Avatar"
            //     className="h-8 w-8 border border-customGreen-primary rounded-full"
            //     onClick={() => navigate("/login")}
            //   />
            // </div>
          }
        </div>
      </nav>
    </>
  );
};

export default Navbar;
